import { ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SelectTriggerOfferModal } from '@components/admission-modal/admission/select-trigger-offer-modal/select-trigger-offer-modal';
import { DatePicker } from '@components/date-picker';
import { InputTime } from '@components/input-time/src/input-time';
import { LabelPrimary } from '@components/label';
import { ModalV2 } from '@components/modals';
import { Select, SelectOption } from '@components/select';
import { DATE_FORMAT_SHORT_TIME } from '@constants/datetime';
import { getAfterOfferDescriptionFromAdmission } from '@utils/get-after-offer-description-from-admission/get-after-offer-description-from-admission';
import { fromBackend } from '@utils/map-date/from-backend';
import classNames from 'classnames';
import { Moment } from 'moment-timezone';

import {
  AdmissionSalesDatesProps,
  SALES_END_TYPE_AT_EVENTS_END,
  SALES_END_TYPE_AT_EVENTS_START,
  SALES_END_TYPE_CUSTOM_DATE,
  SALES_START_TYPE_AFTER_OFFER,
  SALES_START_TYPE_CUSTOM_DATE,
  SALES_START_TYPE_NOW,
  SALES_WINDOW_VARIANT_PLAIN,
  SALES_WINDOW_VARIANT_SELECT,
  SalesEndType,
  SalesStartType,
} from './admission-sales-dates.type';

export const ADMISSION_SALES_DATES_TEST_ID = 'admission-sales-dates';

const tid = (prefix: string, suffix: string) => `${prefix}-${suffix}`;

const SelectFieldGroup = ({
  prefix,
  testid,
  label,
  error,
  children,
}: {
  prefix: string;
  testid: string;
  label: string;
  error?: string;
  children: ReactNode;
}) => (
  <div className='flex flex-col gap-2 min-w-0' role='group' aria-label={label} data-testid={tid(prefix, testid)}>
    <LabelPrimary marginBottomClassName='' customColors='text-secondary'>
      {label}
    </LabelPrimary>
    {children}
    {error && (
      <span data-testid={tid(prefix, `${testid}-error`)} className='text-body-xs text-critical-no-bg'>
        {error}
      </span>
    )}
  </div>
);

export const AdmissionSalesDates = ({
  value,
  onChange,
  context,
  errors,
  disabled = false,
  variant = SALES_WINDOW_VARIANT_SELECT,
  dataTestId = ADMISSION_SALES_DATES_TEST_ID,
}: AdmissionSalesDatesProps) => {
  const { t } = useTranslation();
  const [isTriggerOfferModalOpen, setIsTriggerOfferModalOpen] = useState<boolean>(false);
  const prevSalesStartTypeRef = useRef<SalesStartType>(value.startType);

  useEffect(() => {
    if (
      value.startType === SALES_START_TYPE_AFTER_OFFER &&
      prevSalesStartTypeRef.current !== SALES_START_TYPE_AFTER_OFFER
    ) {
      setIsTriggerOfferModalOpen(true);
    }
    prevSalesStartTypeRef.current = value.startType;
  }, [value.startType]);

  const eventReadOnly = Boolean(context.eventIsInThePast);
  const isReadOnly = disabled || eventReadOnly;

  const handleStartTypeChange = (nextType: SalesStartType) => {
    if (nextType === SALES_START_TYPE_NOW) {
      const next = context.now.clone();
      onChange({
        startType: nextType,
        startDate: next,
        startTime: next.format(DATE_FORMAT_SHORT_TIME),
      });
      return;
    }
    if (nextType !== SALES_START_TYPE_AFTER_OFFER) {
      context.setSelectedOfferTrigger(null);
    }
    onChange({ startType: nextType });
  };

  const handleEndTypeChange = (nextType: SalesEndType) => {
    if (nextType === SALES_END_TYPE_AT_EVENTS_START) {
      const next = fromBackend(context.eventStartingTime).tz(context.timezone);
      onChange({ endType: nextType, endDate: next, endTime: next.format(DATE_FORMAT_SHORT_TIME) });
      return;
    }
    if (nextType === SALES_END_TYPE_AT_EVENTS_END) {
      const next = fromBackend(context.eventEndingTime).tz(context.timezone);
      onChange({ endType: nextType, endDate: next, endTime: next.format(DATE_FORMAT_SHORT_TIME) });
      return;
    }
    onChange({ endType: nextType });
  };

  const handleCloseTriggerOfferModal = (hasTriggerOfferSelected?: boolean) => {
    if (!hasTriggerOfferSelected) {
      context.onTriggerOfferCancel?.();
    }
    setIsTriggerOfferModalOpen(false);
  };

  const setAfterOfferStartDate = (date: number) => {
    const startDate = fromBackend(date).tz(context.timezone);
    onChange({ startDate, startTime: startDate.format(DATE_FORMAT_SHORT_TIME) });
  };

  const renderDateTimeRow = (side: 'start' | 'end', { withFieldLabels = true }: { withFieldLabels?: boolean } = {}) => {
    const isStart = side === 'start';
    const date = isStart ? value.startDate : value.endDate;
    const time = isStart ? value.startTime : value.endTime;
    const onDateChange = (next: Moment | null) => onChange(isStart ? { startDate: next } : { endDate: next });
    const onTimeChange = (next: string) => onChange(isStart ? { startTime: next } : { endTime: next });
    const hasError = isStart ? Boolean(errors?.salesStart) : Boolean(errors?.salesEnd);
    const minBookingDate = isStart ? context.now : value.startDate ?? context.now;

    return (
      <div className='flex flex-row gap-4' data-testid={tid(dataTestId, `${side}-date-row`)}>
        <div className='flex-2 flex flex-col gap-2'>
          {withFieldLabels && (
            <LabelPrimary
              className='text-left self-start'
              marginBottomClassName=''
              hasError={hasError}
              customColors='text-secondary'>
              {t('common:dateLabel')}
            </LabelPrimary>
          )}
          <DatePicker
            id={tid(dataTestId, `${side}-date-picker`)}
            data-testid={tid(dataTestId, `${side}-date-picker`)}
            value={date}
            onChange={onDateChange}
            placeholder={t('common:dateLabel')}
            locale={context.locale}
            timezone={context.timezone}
            minBookingDate={minBookingDate}
            maxBookingDate={context.maxEndDate}
            initialVisibleMonth={date ? () => date : null}
            hasError={hasError}
            disabled={isReadOnly}
            readOnly={eventReadOnly}
          />
        </div>
        <div className='flex-1 flex flex-col gap-2'>
          {withFieldLabels && (
            <LabelPrimary
              className='text-left self-start'
              marginBottomClassName=''
              hasError={hasError}
              customColors='text-secondary'>
              {t('offers:admissionBasicInfoTimeLabel')}
            </LabelPrimary>
          )}
          <InputTime
            id={tid(dataTestId, `${side}-time-input`)}
            data-testid={tid(dataTestId, `${side}-time-input`)}
            name={`sales${isStart ? 'Start' : 'End'}Time`}
            value={time}
            onChange={onTimeChange}
            hasError={hasError}
            disabled={isReadOnly}
          />
        </div>
      </div>
    );
  };

  const renderAfterOfferButton = () => (
    <div className='flex flex-col gap-2' data-testid={tid(dataTestId, 'after-offer-row')}>
      <LabelPrimary
        className='text-left self-start'
        marginBottomClassName=''
        hasError={Boolean(errors?.salesStart)}
        customColors='text-secondary'>
        {t(`offers:salesStart${SALES_START_TYPE_AFTER_OFFER}`)}
      </LabelPrimary>
      <button
        type='button'
        data-testid={tid(dataTestId, 'after-offer-button')}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsTriggerOfferModalOpen(true);
        }}
        className={classNames(
          'w-full min-h-12 border border-soft hover:border-hard rounded-md cursor-pointer text-left p-3 pl-4 text-body-m text-primary bg-surface-primary',
          { 'cursor-not-allowed pointer-events-none opacity-50': isReadOnly }
        )}>
        <div className='truncate mr-4'>
          {context.selectedOfferTrigger
            ? getAfterOfferDescriptionFromAdmission({ offer: context.selectedOfferTrigger })
            : context.afterOfferDescription ?? ''}
        </div>
      </button>
    </div>
  );

  if (variant === SALES_WINDOW_VARIANT_PLAIN) {
    return (
      <>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4 w-full' data-testid={dataTestId}>
          <SelectFieldGroup
            prefix={dataTestId}
            testid='sales-start'
            label={t('passes:salesStartDateLabel')}
            error={errors?.salesStart}>
            {renderDateTimeRow('start', { withFieldLabels: false })}
          </SelectFieldGroup>
          <SelectFieldGroup
            prefix={dataTestId}
            testid='sales-end'
            label={t('passes:salesEndDateLabel')}
            error={errors?.salesEnd}>
            {renderDateTimeRow('end', { withFieldLabels: false })}
          </SelectFieldGroup>
        </div>
        {errors?.globalError && (
          <span data-testid={tid(dataTestId, 'global-error')} className='mt-1 text-body-xs text-critical-no-bg'>
            {errors.globalError}
          </span>
        )}
      </>
    );
  }

  return (
    <>
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 w-full' data-testid={dataTestId}>
        <div className='flex flex-col gap-4'>
          <SelectFieldGroup
            prefix={dataTestId}
            testid='sales-start'
            label={t('offers:salesStartType')}
            error={errors?.salesStart}>
            <Select
              value={value.startType}
              data-testid={tid(dataTestId, 'sales-start-select')}
              onChange={(event) => handleStartTypeChange((event.target as HTMLSelectElement).value as SalesStartType)}
              disabled={isReadOnly}
              readOnly={eventReadOnly}
              hasError={Boolean(errors?.salesStart)}
              selectFontLight>
              <SelectOption value={SALES_START_TYPE_NOW} label={t(`offers:salesStart${SALES_START_TYPE_NOW}`)} />
              <SelectOption
                value={SALES_START_TYPE_CUSTOM_DATE}
                label={t(`offers:salesStart${SALES_START_TYPE_CUSTOM_DATE}`)}
              />
              <SelectOption
                value={SALES_START_TYPE_AFTER_OFFER}
                label={t(`offers:salesStart${SALES_START_TYPE_AFTER_OFFER}`)}
              />
            </Select>
          </SelectFieldGroup>
          {value.startType === SALES_START_TYPE_CUSTOM_DATE && renderDateTimeRow('start')}
          {value.startType === SALES_START_TYPE_AFTER_OFFER && renderAfterOfferButton()}
        </div>
        <div className='flex flex-col gap-4'>
          <SelectFieldGroup
            prefix={dataTestId}
            testid='sales-end'
            label={t('offers:salesEndType')}
            error={errors?.salesEnd}>
            <Select
              value={value.endType}
              data-testid={tid(dataTestId, 'sales-end-select')}
              onChange={(event) => handleEndTypeChange((event.target as HTMLSelectElement).value as SalesEndType)}
              disabled={isReadOnly}
              readOnly={eventReadOnly}
              hasError={Boolean(errors?.salesEnd)}
              selectFontLight>
              <SelectOption
                value={SALES_END_TYPE_AT_EVENTS_START}
                label={t(`offers:salesEnd${SALES_END_TYPE_AT_EVENTS_START}`)}
              />
              <SelectOption
                value={SALES_END_TYPE_AT_EVENTS_END}
                label={t(`offers:salesEnd${SALES_END_TYPE_AT_EVENTS_END}`)}
              />
              <SelectOption
                value={SALES_END_TYPE_CUSTOM_DATE}
                label={t(`offers:salesEnd${SALES_END_TYPE_CUSTOM_DATE}`)}
              />
            </Select>
          </SelectFieldGroup>
          {value.endType === SALES_END_TYPE_CUSTOM_DATE && renderDateTimeRow('end')}
        </div>
      </div>
      <ModalV2
        isOpen={isTriggerOfferModalOpen}
        onClose={() => handleCloseTriggerOfferModal()}
        maxWidthClass='lg:max-w-(--breakpoint-md)'
        zIndex={60}>
        <SelectTriggerOfferModal
          handleClose={handleCloseTriggerOfferModal}
          eventId={context.eventId}
          timezone={context.timezone}
          admissionType={context.offerType}
          setSelectedOfferTrigger={context.setSelectedOfferTrigger}
          offerId={context.offerId}
          setAfterOfferStartDate={setAfterOfferStartDate}
        />
      </ModalV2>

      {errors?.globalError && (
        <span data-testid={tid(dataTestId, 'global-error')} className='mt-1 text-body-xs text-critical-no-bg'>
          {errors.globalError}
        </span>
      )}
    </>
  );
};
